import { ConfigProvider } from 'antd';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getClinicMenu } from '@/shared/layouts/menu/registry';
import { useAuth } from '@shared/providers/AuthContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import type { TenantShellProps } from '../../../types';
import RahClinicFilter from '../organisms/RahClinicFilter';
import RahTopbar, { type RahTile } from '../organisms/RahTopbar';
import RahTopbarActions from '../organisms/RahTopbarActions';
import {
  canAccessRbacSettings,
  enforceRbacSettingsMenuAccess,
} from '../../pages/rbac-settings/access';
import { aggregateClinicKpi, getOwnerClinics } from '../../services/clinicScope';
import RahSidebar from '../../shared/layouts/RahSidebar';
import {
  PermissionProvider,
  usePermissions,
} from '../../shared/providers/PermissionContext';
import {
  RahShellContext,
  type RahShellContextValue,
} from '../../shared/providers/RahShellContext';
import { hitowaAntdTheme } from '../../shared/providers/theme/antdTheme';
import '../../styles/rah-shell.scss';
import '../../styles/rah-shell-additions.scss';
import '../../styles/topbar.scss';

/**
 * RAH clinic-portal shell.
 *
 * PermissionProvider intentionally sits above RahShellFrame. The frame needs
 * the API principal for its role pill and must not paint its menu/content while
 * the effective-permission request is pending or failed.
 */
export const RahShell: React.FC<TenantShellProps> = ({ children }) => {
  const { tenant } = useParams<{ tenant: string }>();
  const { user, clinic } = useAuth();
  const site = tenant ?? clinic?.site_type ?? null;

  return (
    <ConfigProvider theme={hitowaAntdTheme}>
      <PermissionProvider site={site} user={user}>
        <RahShellFrame>{children}</RahShellFrame>
      </PermissionProvider>
    </ConfigProvider>
  );
};

const RahShellFrame: React.FC<TenantShellProps> = ({ children }) => {
  const { tenant } = useParams<{ tenant: string }>();
  const { user, clinic } = useAuth();
  const { serviceType } = useTheme();
  const permission = usePermissions();
  const [functionTiles, setFunctionTiles] = useState<RahTile[]>([]);

  // 担当拠点フィルター — every 拠点 is in scope until the user narrows it.
  const clinics = useMemo(() => getOwnerClinics(), []);
  const [selectedClinics, setSelectedClinics] = useState<string[]>(() =>
    getOwnerClinics().map(clinicItem => clinicItem.name)
  );

  const tenantPrefix = tenant ? `/${tenant}` : '';
  // The menu factory reads the provider's in-memory compatibility projection.
  // Rebuild on each frame render so a version refresh is visible immediately;
  // the tree is small and its renderer remains untouched.
  const menu = enforceRbacSettingsMenuAccess(
    getClinicMenu({
      siteType: clinic?.site_type ?? tenant,
      serviceType,
      family: clinic?.parent_site_type || clinic?.site_type || '',
    }),
    canAccessRbacSettings(permission)
  );

  // R-04 right zone: 施術予定 / 施術実施 / 返戻, counted over the selected 拠点 —
  // the trio is fixed, the numbers are not.
  const systemTiles = useMemo<RahTile[]>(() => {
    const kpi = aggregateClinicKpi(selectedClinics);
    return [
      {
        key: 'planned',
        label: '施術予定',
        icon: '📅',
        value: kpi.planned,
        // R-04: only the leading tile of a zone carries the scope, matching
        // `renderSysTiles` in the ConceptDesign (`#sysScope`).
        unit: `件 (${selectedClinics.length}拠点)`,
        tone: 'var(--info)',
      },
      {
        key: 'done',
        label: '施術実施',
        icon: '✅',
        value: kpi.done,
        unit: '件',
        tone: 'var(--success)',
      },
      {
        key: 'returned',
        label: '返戻',
        icon: '↩',
        value: kpi.returned,
        unit: '件',
        tone: 'var(--danger)',
        title: '返戻 (医療機関・保険者から差戻し)',
      },
    ];
  }, [selectedClinics]);

  const contextValue = useMemo<RahShellContextValue>(
    () => ({ setFunctionTiles, selectedClinics }),
    [selectedClinics]
  );

  const roleLabel = permission.primaryRole?.name || '権限未設定';
  const userName = permission.principal?.name || user?.name || '';

  return (
    <RahShellContext.Provider value={contextValue}>
      <div className="rah-shell">
        <div className="page-layout">
          <RahSidebar
            menu={menu}
            roleLabel={roleLabel}
            userName={userName}
            footerLines={['RAH', '© HITOWA']}
            tenantPrefix={tenantPrefix}
          />

          <div className="main-area">
            <RahTopbar
              menu={menu}
              tenantPrefix={tenantPrefix}
              functionTiles={functionTiles}
              systemTiles={systemTiles}
              actions={<RahTopbarActions />}
              scopeFilter={
                <RahClinicFilter
                  clinics={clinics}
                  selected={selectedClinics}
                  onChange={setSelectedClinics}
                />
              }
            />
            <div className="content-area">{children}</div>
          </div>
        </div>
      </div>
    </RahShellContext.Provider>
  );
};

export default RahShell;
